import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { OnChangeFn, RowSelectionState, SortingState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useState } from 'react';

import { ChevronDown, Minus, Plus } from '@repo/media/lucide';

import { Button } from '@repo/components/button';
import { Card } from '@repo/components/card';
import { Menu, MenuItem, MenuSection, MenuTrigger } from '@repo/components/menu';
import { Popover } from '@repo/components/popover';
import { SearchField } from '@repo/components/search-field';

import { isDefined } from '@repo/utils/is-defined';
import { normalise } from '@repo/utils/normalise';
import { pluralize } from '@repo/utils/pluralize';

import { FeatureGuard } from '~/features/auth/feature-guard.tsx';
import { queryProjectById, queryProjectRoles, queryProjectServiceUsers } from '~/services/authorization/authorization.ts';
import type { ServiceUser } from '~/services/authorization/types.ts';

import { DialogAddServiceAccountsToProject } from './-components/dialog-add-service-accounts-to-project.tsx';
import { DialogAssignRolesBulk } from './-components/dialog-assign-roles-bulk.tsx';
import { DialogRemoveRolesBulk } from './-components/dialog-remove-roles-bulk.tsx';
import { DialogSetRoles } from './-components/dialog-set-roles.tsx';
import { ServiceAccountsTable } from './-components/service-accounts-table.tsx';

const pageSize = 25;

export const Route = createFileRoute('/(user-mgmt)/project/$project/user-mgmt/service-accounts')({
  loader: async ({ context: { queryClient }, params: { project } }) => {
    await Promise.all([
      queryClient.ensureQueryData(queryProjectServiceUsers(project, { page: 1, pageSize })),
      queryClient.ensureQueryData(queryProjectRoles(project)),
    ]);
  },
  component: RouteComponent,
});

type Dialog = { type: 'set' | 'assign-bulk' | 'remove-bulk' | 'add' };

// The active role filter, applied server-side. `roleId` targets a specific custom role;
// `roleType` covers the synthetic "Organization admin" bucket (Admin-type roles), which has
// no entry in the project's custom-role list.
type RoleFilter = { label: string; roleId?: string; roleType?: 'SuperAdmin' | 'Admin' | 'Custom' };

function RouteComponent() {
  const projectId = Route.useParams().project;
  const [dialog, setDialog] = useState<Dialog>();
  const [dialogUser, setDialogUser] = useState<ServiceUser>();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [roleFilter, setRoleFilter] = useState<RoleFilter>();
  const [sorting, setSorting] = useState<SortingState>([]);

  const usersResult = useSuspenseQuery(queryProjectServiceUsers(projectId, {
    searchText: debouncedSearchTerm,
    page: currentPage,
    pageSize,
    roleId: roleFilter?.roleId,
    roleType: roleFilter?.roleType,
    sortBy: sorting[0]?.id as 'name' | 'email' | undefined,
    sortDescending: sorting[0]?.desc,
  })).data;
  const serviceAccounts = usersResult.items;
  const roles = useSuspenseQuery(queryProjectRoles(projectId)).data;
  const rolesById = normalise(roles).byId;
  const project = useSuspenseQuery(queryProjectById(projectId)).data;

  const clearFilter = () => {
    setSearchTerm('');
    setRoleFilter(undefined);
    setCurrentPage(1);
    setRowSelection({});
  };

  // Re-sorting reorders the entire (server-side) result set, so drop back to the first page
  // and clear any page-local selection.
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
    setCurrentPage(1);
    setRowSelection({});
  };

  const selectedUsers = Object.entries(rowSelection)
    .map(([idx, selected]) => (selected ? serviceAccounts[Number(idx)] : undefined))
    .filter(isDefined);
  const selectedCount = selectedUsers.length;
  const userForRoleDialog = dialogUser ?? selectedUsers[0];
  const hasActiveFilters = Boolean(searchTerm || roleFilter);

  return (
    <>
      {dialog && (
        <>
          {userForRoleDialog && (
            <DialogSetRoles
              type="project"
              parentId={projectId}
              roles={roles}
              user={userForRoleDialog}
              open={dialog.type === 'set'}
              close={() => {
                setDialogUser(undefined);
                setDialog(undefined);
              }}
            />
          )}
          <DialogAssignRolesBulk
            open={dialog.type === 'assign-bulk'}
            close={() => {
              setDialog(undefined);
            }}
            roles={roles}
            users={selectedUsers}
            type="project"
            parentId={projectId}
            subjectLabel="service accounts"
          />
          <DialogRemoveRolesBulk
            open={dialog.type === 'remove-bulk'}
            close={() => {
              setDialog(undefined);
            }}
            roles={roles}
            users={selectedUsers}
            type="project"
            parentId={projectId}
            subjectLabel="service accounts"
          />
          {dialog.type === 'add' && (
            <DialogAddServiceAccountsToProject
              orgId={project.organizationId}
              projectId={projectId}
              close={() => {
                setDialog(undefined);
              }}
              roles={roles}
            />
          )}
        </>
      )}

      <div className="flex gap-4 flex-wrap items-center">
        <SearchField
          name="search"
          autoComplete="off"
          aria-label="Search service accounts"
          placeholder="Search service accounts"
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
            setRowSelection({});
          }}
        />
        <MenuTrigger>
          <Button variant="soft" icon={{ component: <ChevronDown className="-m-1" /> }}>
            {roleFilter?.label ?? 'All roles'}
          </Button>
          <Popover>
            <Menu>
              <MenuSection title="Filter role">
                <MenuItem onAction={() => {
                  setRoleFilter(undefined);
                  setCurrentPage(1);
                  setRowSelection({});
                }}
                >
                  All roles
                </MenuItem>
                <MenuItem onAction={() => {
                  setRoleFilter({ label: 'Organization admin', roleType: 'Admin' });
                  setCurrentPage(1);
                  setRowSelection({});
                }}
                >
                  Organization admin
                </MenuItem>
                {roles.map(role => (
                  <MenuItem
                    key={role.id}
                    onAction={() => {
                      setRoleFilter({ label: role.name, roleId: role.id });
                      setCurrentPage(1);
                      setRowSelection({});
                    }}
                  >
                    {role.name}
                  </MenuItem>
                ))}
              </MenuSection>
            </Menu>
          </Popover>
        </MenuTrigger>

        <FeatureGuard features={{ key: 'user-management:manage-users', scope: 'Project' }}>
          {selectedCount === 1
            ? (
                <Button action={() => {
                  setDialogUser(selectedUsers[0]);
                  setDialog({ type: 'set' });
                }}
                >
                  Manage service account
                </Button>
              )
            : selectedCount > 1
              ? (
                  <MenuTrigger>
                    <Button icon={{ component: <ChevronDown className="-m-1" /> }}>
                      Manage service accounts (
                      {pluralize(selectedCount, 'service account')}
                      )
                    </Button>
                    <Popover>
                      <Menu>
                        <MenuItem onAction={() => { setDialog({ type: 'assign-bulk' }); }}>
                          <Plus size={18} />
                          Assign roles
                        </MenuItem>
                        <MenuItem onAction={() => { setDialog({ type: 'remove-bulk' }); }}>
                          <Minus size={18} />
                          Remove roles
                        </MenuItem>
                      </Menu>
                    </Popover>
                  </MenuTrigger>
                )
              : roles.length > 0 && (
                <Button action={() => { setDialog({ type: 'add' }); }}>
                  Add service accounts
                </Button>
              )}
        </FeatureGuard>
      </div>

      <Card className="mt-4 max-w-full w-fit p-0">
        <ServiceAccountsTable
          type="project"
          serviceAccounts={serviceAccounts}
          roles={rolesById}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          currentPage={currentPage}
          pageCount={Math.max(usersResult.totalPages, 1)}
          onPageChange={(page) => {
            setCurrentPage(page);
            setRowSelection({});
          }}
          noResults={(
            <div className="flex flex-col items-start gap-4">
              {hasActiveFilters
                ? (
                    <>
                      <p>Found no service accounts matching the current filter.</p>
                      <Button action={clearFilter}>Clear filter</Button>
                    </>
                  )
                : <p>No service accounts have been added to this site yet</p>}
            </div>
          )}
        />
      </Card>
    </>
  );
}
